import React, { useEffect, useState } from 'react';
import axios from 'axios';
import '../../../shared/StyledInput.css';
import RequiredMark from '../../../shared/RequiredMark';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import NumberInput from '../../../shared/NumberInput';
import PackingProductPicker from '../../../shared/PackingProductPicker';
import { useShipmentDimensionUnit, useShipmentWeightUnit, shipmentDimensionLabel } from '../../../shared/useShipmentWeightUnit';
import { carrierChoices } from '../../../shared/shippingCarriage';
import brandLabel from '../../../shared/brandLabel';
import ToggleSwitch from '../../../shared/ToggleSwitch';
import FieldHelp from '../../../shared/FieldHelp';
import { WEIGHT_TO_KG } from '../packageShared';
import { PACKAGING_TYPES } from './packagingTypes';

const DIMENSIONS = [
  { key: 'length', placeholder: 'L', title: 'Length' },
  { key: 'width', placeholder: 'W', title: 'Width' },
  { key: 'height', placeholder: 'H', title: 'Height' },
];

// A Satchel's Flat Size: length and width only, for reference.
const FLAT = DIMENSIONS.filter(({ key }) => key !== 'height');

const INSIDE = DIMENSIONS.map(({ key, placeholder, title }) => ({ key: `inside_${key}`, placeholder, title: `Inside ${title}` }));

// A Mailer's Flat Size and Max Thickness (issue #363), kept as its inside
// length, width and height.
const MAILER_FLAT = [
  { key: 'inside_length', placeholder: 'L', title: 'Length' },
  { key: 'inside_width', placeholder: 'W', title: 'Width' },
];
const MAX_THICKNESS = [{ key: 'inside_height', placeholder: 'T', title: 'Max Thickness' }];

// Form Section Cards (ui-architecture.md).
const SECTION_HEADER_STYLE = { margin: '0 0 10px', fontSize: 12, fontWeight: 600, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.6 };
const SECTION_STYLE = { padding: 16, background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' };
// Names a field under its box, so it still reads once a value hides the placeholder.
const CAPTION_STYLE = { fontSize: 11, color: '#8a94a6', textAlign: 'center', marginTop: 2 };

const str = (v) => (v === null || v === undefined ? '' : String(v));

const EMPTY_WEIGHT_HELP = 'What the packaging weighs on its own. Optim adds it to the contents to get the shipping weight.';
const MAX_WEIGHT_HELP = 'The most this packaging can hold, packaging included. Optim won’t pack or quote past it, and warns when a package is weighed over it.';
const FLAT_RATE_MAX_HELP = 'Set by the carrier for this flat-rate product. Over it, the package doesn’t qualify for the flat rate.';

// A weight caption with its "?" beside it.
const weightCaption = (text, fieldKey, label, help) => (
  <div style={{ ...CAPTION_STYLE, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
    {text}
    <FieldHelp fieldKey={fieldKey} pageKey="warehouse-packaging" articleSlug="packaging" articleTitle="Packaging" label={label}>{help}</FieldHelp>
  </div>
);

// Add/Edit one Packaging (issue #299): a name, plus default dimensions in
// the Shipment Dimension Unit that fill in when it's picked on a package,
// and optionally the Consumable Product it's bought as. Its Brand is that
// Product's, shown read-only (#386); unlinked, it has none.
// Issue #314: its Empty and Maximum Weight in the Shipment Weight Unit,
// and whether it's a Carrier's own flat-rate product, picked from one list
// grouped by Carrier (the Carrier is saved with it). A flat-rate product's
// Maximum Weight is its carrier's limit, shown read-only.
// The dimensions are its outside size; its inside size (optional, as box
// suppliers quote it) is what the Virtual Packer packs into.
// Issue #317: its Type (required). A Satchel has no inside size; a Box
// has Can ship on its own. A Satchel's size is its Flat Size (length and
// width), kept for reference only: it isn't packed, filled in or priced by.
// Issue #363: a Mailer has no outside size; its Flat Size and Max
// Thickness are what it's packed by.
function PackagingForm({ initial, brands = [], onSave, onCancel, loading }) {
  const dimensionUnit = useShipmentDimensionUnit();
  const weightUnit = useShipmentWeightUnit();
  const [name, setName] = useState(initial?.name || '');
  const [type, setType] = useState(initial?.packaging_type || '');
  const [canShipAlone, setCanShipAlone] = useState(initial ? initial.can_ship_alone !== false : true);
  const [dims, setDims] = useState({ length: str(initial?.length), width: str(initial?.width), height: str(initial?.height) });
  const [inside, setInside] = useState({ inside_length: str(initial?.inside_length), inside_width: str(initial?.inside_width), inside_height: str(initial?.inside_height) });
  const [product, setProduct] = useState(initial?.product || null);
  const productBrand = product?.brand_id ? brands.find((b) => String(b.id) === String(product.brand_id)) || product.brand : null;
  const [emptyWeight, setEmptyWeight] = useState(str(initial?.empty_weight));
  const [maxWeight, setMaxWeight] = useState(str(initial?.max_weight));
  // "carrierId|productKey", or '' for the business's own packaging.
  const [flatRate, setFlatRate] = useState(initial?.carrier_id && initial?.flat_rate_product ? `${initial.carrier_id}|${initial.flat_rate_product}` : '');
  const [services, setServices] = useState([]);
  const [flatRateGroups, setFlatRateGroups] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get('/api/shipping-services', { params: { status: 'all' } })
      .then((res) => setServices(res.data.data || []))
      .catch(() => setServices([]));
  }, []);

  // Carriers that have a Service set up, without No Carrier, plus the one
  // this Packaging already has if it's no longer among them. Each with the
  // flat-rate products Optim knows for it; Carriers with none are left out.
  const carrierKey = (list) => list.map((c) => c.id).join(',');
  const carriers = carrierChoices(services, initial?.carrier_id).filter((c) => c.system_key !== 'no_carrier');
  if (initial?.carrier && !carriers.some((c) => String(c.id) === String(initial.carrier.id))) carriers.push(initial.carrier);
  const carriersKey = carrierKey(carriers);

  useEffect(() => {
    let cancelled = false;
    Promise.all(carriers.map((c) => axios.get('/api/packagings/flat-rate-products', { params: { carrier_id: c.id } })
      .then((res) => ({ carrier: c, products: res.data.data || [] }))
      .catch(() => ({ carrier: c, products: [] }))))
      .then((groups) => { if (!cancelled) setFlatRateGroups(groups.filter((g) => g.products.length)); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carriersKey]);

  // The picked flat-rate product's carrier and weight limit, in the
  // Shipment Weight Unit; null for own packaging.
  const [flatCarrierId, flatKey] = flatRate ? flatRate.split('|') : [null, null];
  const flatGroup = flatRateGroups.find((g) => String(g.carrier.id) === flatCarrierId);
  const flatProduct = flatGroup?.products.find((p) => p.key === flatKey);
  const flatMax = flatProduct?.maxKg != null ? flatProduct.maxKg / (WEIGHT_TO_KG[weightUnit] ?? 1) : null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return setError('Name is required.');
    if (!type) return setError('Type is required.');
    const hasInside = type !== 'satchel';
    const mailer = type === 'mailer';
    const insideGiven = hasInside ? INSIDE.filter(({ key }) => inside[key] !== '').length : 0;
    if (insideGiven && insideGiven < 3) return setError(mailer ? 'Enter the Flat Size and Max Thickness, or none.' : 'Enter all three inside dimensions, or none.');
    if (mailer && insideGiven && Number(inside.inside_height) >= Math.min(Number(inside.inside_length), Number(inside.inside_width))) {
      return setError('Max Thickness must be less than the Flat Length and Flat Width.');
    }
    if (!flatRate && emptyWeight !== '' && maxWeight !== '' && Number(maxWeight) <= Number(emptyWeight)) return setError('Maximum Weight must be more than Empty Weight.');
    setError('');
    const val = (v) => (v === '' ? null : v);
    const [carrierId, flatRateProduct] = flatRate ? flatRate.split('|') : [null, null];
    return onSave({
      name: name.trim(),
      packaging_type: type,
      can_ship_alone: type !== 'box' || canShipAlone,
      length: mailer ? null : val(dims.length),
      width: mailer ? null : val(dims.width),
      height: mailer || type === 'satchel' ? null : val(dims.height),
      inside_length: hasInside ? val(inside.inside_length) : null,
      inside_width: hasInside ? val(inside.inside_width) : null,
      inside_height: hasInside ? val(inside.inside_height) : null,
      empty_weight: val(emptyWeight),
      max_weight: flatRate ? null : val(maxWeight),
      carrier_id: carrierId,
      flat_rate_product: flatRateProduct,
      product_id: product ? product.id : null,
    });
  };

  const dimensionInputs = (fields, values, setValues) => (
    <div style={{ display: 'flex', gap: 8, width: 260 }}>
      {fields.map(({ key, placeholder, title }) => (
        <div key={key} style={{ flex: 1, minWidth: 0 }}>
          <NumberInput
            decimals={3}
            step="0.001"
            min={0}
            placeholder={placeholder}
            title={title}
            value={values[key]}
            onChange={(e) => setValues((prev) => ({ ...prev, [key]: e.target.value }))}
            style={{ width: '100%' }}
          />
          <div style={CAPTION_STYLE}>{title.replace('Inside ', '')}</div>
        </div>
      ))}
    </div>
  );

  const sizeFields = () => {
    if (type === 'satchel') {
      return (
        <div>
          <label>{`Flat Size ${shipmentDimensionLabel(dimensionUnit)}`}</label><br />
          {dimensionInputs(FLAT, dims, setDims)}
          <div style={{ ...CAPTION_STYLE, textAlign: 'left', marginTop: 4, maxWidth: 260 }}>Reference only. Shipping dimension depends on the Box in the Satchel.</div>
        </div>
      );
    }
    if (type === 'mailer') {
      return (
        <div>
          <label>{dimensionUnit ? `Flat Size & Thickness (${dimensionUnit})` : "Flat Size & Thickness"}</label><br />
          {dimensionInputs([...MAILER_FLAT, ...MAX_THICKNESS], inside, setInside)}
          <div style={{ ...CAPTION_STYLE, textAlign: 'left', marginTop: 4, maxWidth: 260 }}>
            Length and width laid flat, as the carrier quotes them. Max Thickness: how thick the contents can be before it won't seal.
          </div>
        </div>
      );
    }
    return (
      <>
        <div>
          <label>{`Outside ${shipmentDimensionLabel(dimensionUnit)}`}</label><br />
          {dimensionInputs(DIMENSIONS, dims, setDims)}
        </div>
        <div>
          <label>{`Inside ${shipmentDimensionLabel(dimensionUnit)}`}</label><br />
          {dimensionInputs(INSIDE, inside, setInside)}
        </div>
      </>
    );
  };

  return (
    <form onSubmit={handleSubmit} style={{ minWidth: 320 }}>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'stretch' }}>
        <div style={SECTION_STYLE}>
          <h4 style={SECTION_HEADER_STYLE}>Packaging</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label>Name<RequiredMark /></label><br />
              <input
                className="styled-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={100}
                placeholder="e.g., Medium Box"
                style={{ width: 260 }}
              />
            </div>
            <div>
              <label>Type<RequiredMark /></label><br />
              <select className="styled-input" value={type} onChange={(e) => setType(e.target.value)} style={{ width: 260 }}>
                <option value="">(pick one)</option>
                {PACKAGING_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
            {type === 'box' && (
              <div>
                <ToggleSwitch checked={canShipAlone} onChange={setCanShipAlone} label="Can ship on its own" />
                <div style={{ ...CAPTION_STYLE, textAlign: 'left', marginTop: 4, maxWidth: 260 }}>
                  {canShipAlone ? 'Can go out the door as it is.' : 'An inner box; always packed inside something else.'}
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={SECTION_STYLE}>
          <h4 style={SECTION_HEADER_STYLE}>Size &amp; Weight</h4>
          <div style={{ fontSize: 12, color: '#888', margin: '-4px 0 10px' }}>
            Figures typed here are Estimates. Actuals are recorded on Warehouse › Weigh &amp; Measure.
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {sizeFields()}
            <div>
              <label>{weightUnit ? `Weight (${weightUnit})` : 'Weight'}</label><br />
              <div style={{ display: 'flex', gap: 8, width: 260 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <NumberInput
                    decimals={3}
                    step="0.001"
                    min={0}
                    placeholder="Empty"
                    title="Empty Weight"
                    value={emptyWeight}
                    onChange={(e) => setEmptyWeight(e.target.value)}
                    style={{ width: '100%' }}
                  />
                  {weightCaption('Empty', 'warehouse-packaging.empty-weight', 'Packaging — Empty Weight', EMPTY_WEIGHT_HELP)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {flatRate ? (
                    <>
                      <input
                        className="styled-input"
                        readOnly
                        disabled
                        title="Maximum Weight"
                        value={flatMax == null ? '' : String(Number(flatMax.toFixed(3)))}
                        style={{ width: '100%' }}
                      />
                      {weightCaption(`Set by ${flatGroup?.carrier.name || 'the carrier'}`, 'warehouse-packaging.flat-rate-max-weight', 'Packaging — Maximum Weight (flat-rate product)', FLAT_RATE_MAX_HELP)}
                    </>
                  ) : (
                    <>
                      <NumberInput
                        decimals={3}
                        step="0.001"
                        min={0}
                        placeholder="Maximum"
                        title="Maximum Weight"
                        value={maxWeight}
                        onChange={(e) => setMaxWeight(e.target.value)}
                        style={{ width: '100%' }}
                      />
                      {weightCaption('Maximum', 'warehouse-packaging.max-weight', 'Packaging — Maximum Weight', MAX_WEIGHT_HELP)}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={SECTION_STYLE}>
          <h4 style={SECTION_HEADER_STYLE}>Carrier &amp; Purchasing</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label>Carrier Flat-Rate Product</label><br />
              <select className="styled-input" value={flatRate} onChange={(e) => {
                setFlatRate(e.target.value);
                if (!type && e.target.value.includes('satchel')) setType('satchel');
              }} style={{ width: 300 }}>
                <option value="">(own packaging)</option>
                {flatRateGroups.map(({ carrier, products }) => (
                  <optgroup key={carrier.id} label={carrier.name}>
                    {products.map((p) => <option key={p.key} value={`${carrier.id}|${p.key}`}>{p.name}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
            <div>
              <label>Consumable Product</label><br />
              <PackingProductPicker
                value={product}
                onChange={setProduct}
                style={{ width: 300 }}
              />
            </div>
            <div>
              <label>Brand</label><br />
              <input
                className="styled-input"
                readOnly
                disabled
                title={productBrand ? brandLabel(productBrand) : 'Brand'}
                value={productBrand ? productBrand.name : ''}
                style={{ width: 300 }}
              />
              <div style={{ ...CAPTION_STYLE, textAlign: 'left' }}>
                {product ? 'From the Consumable Product' : 'Link a Consumable Product to show its Brand'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}

      <div style={{ marginTop: 12 }}>
        <SaveButton loading={loading} />
        <CancelButton onClick={onCancel} disabled={loading} style={{ marginLeft: 8 }} />
      </div>
    </form>
  );
}

export default PackagingForm;
